/**
 * Line diff for the diff view of edits (06 §15): the longest common
 * subsequence of lines, with a plain "removed, then added" fallback for very
 * large inputs so the browser never stalls.
 */
export interface DiffLine {
  kind: 'same' | 'add' | 'del';
  text: string;
}

const MAX_CELLS = 400_000;

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.length > 0 ? before.replace(/\r\n?/g, '\n').split('\n') : [];
  const b = after.length > 0 ? after.replace(/\r\n?/g, '\n').split('\n') : [];
  if (a.length * b.length > MAX_CELLS) return [...a.map((text) => ({ kind: 'del' as const, text })), ...b.map((text) => ({ kind: 'add' as const, text }))];
  // lcs[i][j]: common lines of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      (lcs[i] as number[])[j] = a[i] === b[j] ? ((lcs[i + 1] as number[])[j + 1] as number) + 1 : Math.max((lcs[i + 1] as number[])[j] as number, (lcs[i] as number[])[j + 1] as number);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] as string });
      i += 1;
      j += 1;
    } else if (((lcs[i + 1] as number[])[j] as number) >= ((lcs[i] as number[])[j + 1] as number)) {
      out.push({ kind: 'del', text: a[i] as string });
      i += 1;
    } else {
      out.push({ kind: 'add', text: b[j] as string });
      j += 1;
    }
  }
  for (; i < a.length; i += 1) out.push({ kind: 'del', text: a[i] as string });
  for (; j < b.length; j += 1) out.push({ kind: 'add', text: b[j] as string });
  return out;
}

/** The before and after of an edit or a write, from the call's arguments. */
export function changeOf(tool: string, rawArguments: string): { path: string; before: string; after: string } | null {
  let input: Record<string, unknown>;
  try {
    input = JSON.parse(rawArguments || '{}') as Record<string, unknown>;
  } catch {
    return null;
  }
  const path = typeof input.path === 'string' ? input.path : '';
  if (tool === 'edit' && typeof input.old_string === 'string' && typeof input.new_string === 'string') {
    return { path, before: input.old_string, after: input.new_string };
  }
  if (tool === 'write_file' && typeof input.content === 'string') return { path, before: '', after: input.content };
  // 16b.1: an append adds its part at the end of the file
  if (tool === 'append_file' && typeof input.content === 'string') return { path, before: '', after: input.content };
  return null;
}
